import { useCallback } from 'react';
import { useQueries, useQueryClient } from '@tanstack/react-query';
import useExperienceGraphs from 'pages/AnalyticsV2/CustomAnalytics/hooks/useExperienceGraphs';
import { ModuleConfig, ChartData } from 'pages/AnalyticsV2/types';
import experienceGraphService from 'services/api/experience-graph.api';

type UseExperienceGraphsDataResult = {
  graphs: ModuleConfig[];
  isGraphsLoading: boolean;
  isGraphsError: boolean;
  isGraphsFetching: boolean;
  refetchGraphs: () => void;
  graphsData: ChartData[];
  isGraphsDataLoading: boolean;
  graphsDataErrors: boolean[];
  graphsDataFetching: boolean[];
  refetchGraphData: (graphId: string) => void;
};

export default function useExperienceGraphsData(): UseExperienceGraphsDataResult {
  const queryClient = useQueryClient();
  const {
    graphs,
    isLoading: isGraphsLoading,
    isError: isGraphsError,
    isFetching: isGraphsFetching,
    refetch: refetchGraphs,
  } = useExperienceGraphs();

  const graphDataQueries = useQueries({
    queries: graphs.map((graph) => ({
      queryKey: ['experience-graph-data', { id: graph.id }],
      queryFn: () => experienceGraphService.getExperienceGraphData(graph.id),
      refetchOnWindowFocus: false,
      retry: false,
      enabled: !!graph.id,
    })),
  });

  const graphsData = graphs.map((_, index) => {
    const { data } = graphDataQueries[index];

    return data || ({ data: [], moduleConfig: graphs[index] } as unknown as ChartData);
  });

  const graphsDataErrors = graphDataQueries.map((query) => query.isError);
  const graphsDataFetching = graphDataQueries.map((query) => query.isFetching);

  const isGraphsDataLoading = graphDataQueries.some((query) => query.isLoading);

  const refetchGraphData = useCallback(
    (graphId: string) => {
      queryClient.refetchQueries({ queryKey: ['experience-graph-data', { id: graphId }] });
    },
    [queryClient],
  );

  return {
    graphs,
    isGraphsLoading,
    isGraphsError,
    isGraphsFetching,
    refetchGraphs,
    graphsData,
    isGraphsDataLoading,
    graphsDataErrors,
    graphsDataFetching,
    refetchGraphData,
  };
}
